import { useEffect } from 'react'
import { useCreateContractContext, useUserLanguage } from '@/customHooks'
import { LeaseTermOption } from '@/graphql/generated'
import MarkedCalendar from '@/assets/contract-icons/maked-calendar-icon.svg'
import Calendar from '@/assets/contract-icons/calendar-icon.svg'
import textsData from '@/textData/CreateContractsTexts.json'
import { NormalFormDateField, SquareButtonsCard } from '../molecules'

export const SelectContractLeaseTerm = () => {
  const userLanguage = useUserLanguage();

  const texts = userLanguage === 'es' ? textsData.es.leaseTerms : textsData.en.leaseTerms

  const {
    state: contractState,
    setLeaseTerm,
    setButtonEnabled,
  } = useCreateContractContext()

  const isEndDateDisabled = contractState.leaseTermOption === LeaseTermOption.MonthToMonth;
  
  const buttons = [
    { text: texts.button.first, icon: MarkedCalendar },
    { text: texts.button.second, icon: Calendar },
    { text: texts.button.third, icon: Calendar },
  ];

  const defaultSelectedIndex = {
    [LeaseTermOption.FixedTerm]: 0,
    [LeaseTermOption.MonthToMonth]: 1,
    [LeaseTermOption.YearlyTerm]: 2,
  }[contractState.leaseTermOption]

  const onSelectedHandle = (value: number | null) => {
    switch (value) {
      case 0: // Fixed Term
        setLeaseTerm(LeaseTermOption.FixedTerm, contractState.startDate as Date, contractState.finishDate as Date | null)
        break
      
      case 1: { // Month to Month
        const startDate = new Date(contractState.startDate as Date);
        const finishDate = new Date(new Date(startDate).setUTCMonth(startDate.getUTCMonth() + 1));
        setLeaseTerm(LeaseTermOption.MonthToMonth, contractState.startDate as Date, finishDate)
        break
      }

      case 2: { // Yearly Term
        const startDate = new Date(contractState.startDate as Date);
        const finishDate = new Date(new Date(startDate).setUTCFullYear(startDate.getUTCFullYear() + 1));
        setLeaseTerm(LeaseTermOption.YearlyTerm, contractState.startDate as Date, finishDate)
        break
      }
      
      default:
        throw new Error('Select value for propertyType is invalid [SelectPropertyTypeMenuCard]')
    }
  }

  const handleDateChange = (field: 'startDate' | 'finishDate', value: string | null) => {
    const newStartDate = field === 'startDate' && value ? new Date(value) : contractState.startDate as Date;
    let newEndDate = field === 'finishDate' && value ? new Date(value) : contractState.finishDate as Date | null;

    // If Yearly Term is selected and start date changes, update end date automatically
    if (contractState.leaseTermOption === LeaseTermOption.YearlyTerm && field === 'startDate') {
      const startDate = new Date(newStartDate);
      newEndDate = new Date(new Date(startDate).setUTCFullYear(startDate.getUTCFullYear() + 1));
    }

    // If Month to Month is selected and start date changes, update end date automatically
    if (contractState.leaseTermOption === LeaseTermOption.MonthToMonth && field === 'startDate') {
      const startDate = new Date(newStartDate);
      newEndDate = new Date(new Date(startDate).setUTCMonth(startDate.getUTCMonth() + 1));
    }

    setLeaseTerm(contractState.leaseTermOption, newStartDate, newEndDate);
  }
  
  useEffect(() => {
    setButtonEnabled()
  }, [setButtonEnabled])

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center w-full">
        <SquareButtonsCard
          buttons={buttons}
          defaultSelected={defaultSelectedIndex}
          onSelected={onSelectedHandle}
        />
      </div>
      
      <div className="flex justify-center w-full mt-12">
        <form className="bg-white rounded-[18px] p-8 shadow-[0_5px_10px_rgba(0,0,0,0.1)] w-full max-w-[700px]">
          <NormalFormDateField
            label={texts.form.startDate.label}
            name={texts.form.startDate.name}
            value={(contractState.startDate as Date).toISOString()}
            onChange={(value) => handleDateChange('startDate', value)}
            required
          />
          <NormalFormDateField
            label={texts.form.endDate.label}
            name={texts.form.endDate.name}
            value={contractState.finishDate ? (contractState.finishDate as Date).toISOString() : ''}
            onChange={(value) => handleDateChange('finishDate', value)}
            disabled={isEndDateDisabled}
          />
        </form>
      </div>
    </div>
  );
};
